/**
 * Grading engine — pure, framework-free logic for comparing what a student
 * played against a lesson's target. No React, no audio; just the music maths,
 * so it can be unit-reasoned and reused by the practice widget and (later) the
 * sight-reading drill engine.
 *
 * Three target shapes cover every Piano Foundation / Intermediate technique
 * lesson we grade:
 *
 *   • "sequence" — play these pitches IN ORDER (scales, arpeggios, five-finger
 *     drills, melodies). The canonical "wait mode": the cursor only advances on
 *     the correct next note; a wrong note is an error but doesn't advance.
 *   • "chord"    — play these pitches TOGETHER (triads, inversions). Success =
 *     the held set equals the target set.
 *   • "set"      — play all these pitches in ANY order (scale-tone round-ups,
 *     pentatonic pools). Order-free; each unique target hit once.
 *
 * Pitches are Scientific Pitch Notation ("C4", "F#5", "Bb2"). Matching is by
 * PITCH CLASS by default (octave-agnostic) so a student playing the right note
 * an octave away is still correct — the encouraging, Simply-Piano-style choice.
 * Pass `octaveSensitive` when register actually matters.
 */

export type GradeMode = "sequence" | "chord" | "set";

const LETTER_PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** Parse SPN → { pc, midi }. Returns null for an unparseable pitch. */
export function parsePitch(p: string): { pc: number; midi: number } | null {
  const m = p.match(/^([A-G])(#{1,2}|b{1,2})?(-?\d+)$/);
  if (!m) return null;
  const [, letter, acc = "", octStr] = m;
  // Compute midi from the UNWRAPPED offset so spellings that cross the octave
  // boundary keep their register: B#4 is midi 72 (sounds C5), Cb4 is midi 59.
  let raw = LETTER_PC[letter];
  for (const ch of acc) raw += ch === "#" ? 1 : -1;
  const octave = parseInt(octStr, 10);
  const midi = (octave + 1) * 12 + raw;
  const pc = ((raw % 12) + 12) % 12;
  return { pc, midi };
}

/** A note the student actually played (from MIDI or the on-screen keyboard). */
export interface PlayedNote {
  midi: number;
  pc: number;
}

export interface GradeOptions {
  /** If true, the exact octave must match, not just the pitch class. */
  octaveSensitive?: boolean;
}

function matches(target: { pc: number; midi: number }, played: PlayedNote, octaveSensitive?: boolean): boolean {
  return octaveSensitive ? target.midi === played.midi : target.pc === played.pc;
}

// ─────────────────────────────────────────────────────────────────────────────
// SEQUENCE — ordered, wait-mode
// ─────────────────────────────────────────────────────────────────────────────

export interface SequenceState {
  /** Parsed target, with a per-step status the UI colours from. */
  steps: { pitch: string; pc: number; midi: number; status: "pending" | "current" | "correct" }[];
  cursor: number; // index of the next expected note
  errors: number; // total wrong presses
  hits: number; // total correct presses
  done: boolean;
}

export function initSequence(targetPitches: string[]): SequenceState {
  const steps = targetPitches.map((pitch, i) => {
    const parsed = parsePitch(pitch);
    return {
      pitch,
      pc: parsed?.pc ?? -1,
      midi: parsed?.midi ?? -1,
      status: (i === 0 ? "current" : "pending") as "pending" | "current" | "correct",
    };
  });
  return { steps, cursor: 0, errors: 0, hits: 0, done: steps.length === 0 };
}

/**
 * Feed one played note into a sequence. Returns the NEXT state plus a verdict
 * the UI uses to flash green/red. Pure — never mutates the input state.
 */
export function feedSequence(
  state: SequenceState,
  played: PlayedNote,
  opts: GradeOptions = {}
): { state: SequenceState; verdict: "correct" | "wrong"; matchedIndex: number | null } {
  if (state.done) return { state, verdict: "wrong", matchedIndex: null };
  const expected = state.steps[state.cursor];
  if (matches(expected, played, opts.octaveSensitive)) {
    const steps = state.steps.map((s, i) =>
      i === state.cursor ? { ...s, status: "correct" as const } : s
    );
    const nextCursor = state.cursor + 1;
    const done = nextCursor >= steps.length;
    if (!done) steps[nextCursor] = { ...steps[nextCursor], status: "current" };
    return {
      state: { steps, cursor: nextCursor, errors: state.errors, hits: state.hits + 1, done },
      verdict: "correct",
      matchedIndex: state.cursor,
    };
  }
  return {
    state: { ...state, errors: state.errors + 1 },
    verdict: "wrong",
    matchedIndex: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SET — order-free pool
// ─────────────────────────────────────────────────────────────────────────────

export interface SetState {
  steps: { pitch: string; pc: number; midi: number; status: "pending" | "correct" }[];
  errors: number;
  hits: number;
  done: boolean;
}

export function initSet(targetPitches: string[]): SetState {
  const steps = targetPitches.map((pitch) => {
    const parsed = parsePitch(pitch);
    return { pitch, pc: parsed?.pc ?? -1, midi: parsed?.midi ?? -1, status: "pending" as const };
  });
  return { steps, errors: 0, hits: 0, done: steps.length === 0 };
}

export function feedSet(
  state: SetState,
  played: PlayedNote,
  opts: GradeOptions = {}
): { state: SetState; verdict: "correct" | "wrong" | "repeat"; matchedIndex: number | null } {
  const idx = state.steps.findIndex(
    (s) => s.status === "pending" && matches(s, played, opts.octaveSensitive)
  );
  if (idx >= 0) {
    const steps = state.steps.map((s, i) => (i === idx ? { ...s, status: "correct" as const } : s));
    const done = steps.every((s) => s.status === "correct");
    return { state: { ...state, steps, hits: state.hits + 1, done }, verdict: "correct", matchedIndex: idx };
  }
  // Already-correct note replayed → harmless repeat, not an error.
  const already = state.steps.some((s) => s.status === "correct" && matches(s, played, opts.octaveSensitive));
  if (already) return { state, verdict: "repeat", matchedIndex: null };
  return { state: { ...state, errors: state.errors + 1 }, verdict: "wrong", matchedIndex: null };
}

// ─────────────────────────────────────────────────────────────────────────────
// CHORD — simultaneous
// ─────────────────────────────────────────────────────────────────────────────

export interface ChordTarget {
  pitches: string[];
  parsed: { pc: number; midi: number }[];
}

export function initChord(targetPitches: string[]): ChordTarget {
  return {
    pitches: targetPitches,
    parsed: targetPitches.map(parsePitch).filter((p): p is { pc: number; midi: number } => p != null),
  };
}

/**
 * Evaluate the currently-held set of notes against a chord target.
 * `missing` and `extra` drive the UI hints; `correct` is true only when the
 * held set is exactly the target set (no missing, no extra).
 */
export function evaluateChord(
  target: ChordTarget,
  heldMidis: number[],
  opts: GradeOptions = {}
): { correct: boolean; missingIndices: number[]; matchedIndices: number[]; extra: number[] } {
  const held: PlayedNote[] = heldMidis.map((m) => ({ midi: m, pc: ((m % 12) + 12) % 12 }));
  const matchedIndices: number[] = [];
  const missingIndices: number[] = [];
  const usedHeld = new Set<number>();

  target.parsed.forEach((t, i) => {
    const hitAt = held.findIndex(
      (h, hi) => !usedHeld.has(hi) && matches(t, h, opts.octaveSensitive)
    );
    if (hitAt >= 0) {
      matchedIndices.push(i);
      usedHeld.add(hitAt);
    } else {
      missingIndices.push(i);
    }
  });

  // In pitch-class (octave-forgiving) mode, a held note that doubles an
  // already-matched target pc is the most common real voicing (e.g. the root
  // an octave up) — treat it as harmless, not as a wrong note. Only notes
  // OUTSIDE the chord's pitch classes count as extras.
  const targetPcs = new Set(target.parsed.map((t) => t.pc));
  const extra = heldMidis
    .filter((_, hi) => !usedHeld.has(hi))
    .filter((m) => (opts.octaveSensitive ? true : !targetPcs.has(((m % 12) + 12) % 12)));
  return {
    correct: missingIndices.length === 0 && extra.length === 0,
    missingIndices,
    matchedIndices,
    extra,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SCORING
// ─────────────────────────────────────────────────────────────────────────────

/** Accuracy 0..100 from hits and errors. */
export function accuracy(hits: number, errors: number): number {
  const total = hits + errors;
  if (total === 0) return 100;
  return Math.round((hits / total) * 100);
}

/** Star rating 0..3 from an accuracy percentage. */
export function stars(acc: number): number {
  if (acc >= 95) return 3;
  if (acc >= 80) return 2;
  if (acc >= 60) return 1;
  return 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// INTERVAL — what ear training actually teaches
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Judge a sung interval by the DISTANCE between two notes, never by which
 * notes they were. That is the whole point of relative pitch: a student who
 * sings a perfect 5th starting from D has sung a perfect 5th, and marking
 * them wrong because the app happened to play it from C would be teaching
 * them the opposite of the skill.
 *
 * Direction is ignored — singing down a 5th demonstrates the same distance,
 * and a beginner's voice often cannot reach the ascending one.
 *
 * A compound answer (the right interval an octave out, e.g. a 12th for a 5th)
 * is reported separately: it is the right interval and the wrong register, and
 * saying so teaches more than a bare "wrong".
 */
export type IntervalVerdict = "correct" | "compound" | "wrong";

export function judgeInterval(
  firstMidi: number,
  secondMidi: number,
  targetSemitones: number,
): { verdict: IntervalVerdict; sungSemitones: number; offBy: number } {
  const sung = Math.abs(secondMidi - firstMidi);
  const target = Math.abs(targetSemitones);
  if (sung === target) return { verdict: "correct", sungSemitones: sung, offBy: 0 };
  // Same pitch class distance, an octave or more out.
  if (sung % 12 === target % 12 && sung !== 0) {
    return { verdict: "compound", sungSemitones: sung, offBy: sung - target };
  }
  return { verdict: "wrong", sungSemitones: sung, offBy: sung - target };
}

/** Interval name for a semitone distance, for telling a student what they sang. */
export const INTERVAL_NAMES = [
  "unison", "minor 2nd", "major 2nd", "minor 3rd", "major 3rd", "perfect 4th",
  "tritone", "perfect 5th", "minor 6th", "major 6th", "minor 7th", "major 7th", "octave",
] as const;

export function intervalName(semitones: number): string {
  const n = Math.abs(semitones);
  if (n <= 12) return INTERVAL_NAMES[n];
  const within = n % 12;
  return `${INTERVAL_NAMES[within]} + ${Math.floor(n / 12)} octave${n >= 24 ? "s" : ""}`;
}
